import React from 'react';
import { useLanguage, translateText } from '../i18n/Language';
import { localizedUrl } from '../i18n/paths';

const ORIGIN = 'https://hiretempotravellersatna.com';
const imageUrl = value => value && value.startsWith('/') ? ORIGIN + value : value;
const translatedFields = new Set(['name', 'description', 'headline', 'text', 'articleBody', 'caption', 'alternativeHeadline']);
const webPageTypes = new Set(['WebPage', 'AboutPage', 'ContactPage', 'CollectionPage', 'FAQPage', 'ItemPage', 'ProfilePage', 'SearchResultsPage']);
const pageEntityTypes = new Set([...webPageTypes, 'Service', 'BreadcrumbList', 'Article', 'BlogPosting']);
const typesOf = node => [].concat(node?.['@type'] || []);
function schemaNodes(value) {
  if (Array.isArray(value)) return value.flatMap(schemaNodes);
  if (!value || typeof value !== 'object') return [];
  return [value, ...Object.values(value).filter(item => item && typeof item === 'object').flatMap(schemaNodes)];
}

// Localize identified page entities and their references while preserving
// the shared business, website and provider identities.
export function localizeSchema(value, language, key = '', pageIds) {
  if (!pageIds) {
    pageIds = new Set();
    for (const node of schemaNodes(value)) {
      if (!typesOf(node).some(type => pageEntityTypes.has(type)) || typeof node['@id'] !== 'string') continue;
      try {
        const url = new URL(node['@id']);
        if (url.origin === ORIGIN && (url.pathname !== '/' || !typesOf(node).includes('Service'))) pageIds.add(node['@id']);
      } catch (_) { /* Relative and external identifiers stay unchanged. */ }
    }
  }
  if (Array.isArray(value)) return value.map(item => localizeSchema(item, language, key, pageIds));
  if (value && typeof value === 'object') return Object.fromEntries(Object.entries(value)
    .map(([name, item]) => [name, localizeSchema(item, language, name, pageIds)]));
  if (typeof value !== 'string') return value;
  if (key === 'inLanguage') return language === 'hi' ? 'hi-IN' : 'en-IN';
  if (translatedFields.has(key)) return translateText(value, language);
  if (key === '@id') return pageIds.has(value) ? localizedUrl(value, language) : value;
  if (key === 'url' || key === 'mainEntityOfPage' || key === 'item') return localizedUrl(value, language);
  return value;
}

export default function Seo({ title, description, canonical, keywords, robots, image = '/og-cover.jpg', imageAlt, ogType = 'website', schemas = [] }) {
  const { language } = useLanguage();
  const t = value => translateText(value, language);
  const canonicalUrl = localizedUrl(canonical, language);
  const english = localizedUrl(canonical, 'en'), hindi = localizedUrl(canonical, 'hi');
  const noindex = /\bnoindex\b/i.test(robots || '');
  const effectiveRobots = robots || 'index, follow, max-image-preview:large';
  const nodes = schemaNodes(schemas);
  const primary = nodes.find(node => typesOf(node).some(type => ['Service', 'Article', 'BlogPosting'].includes(type)) && node['@id']);
  const breadcrumb = nodes.find(node => typesOf(node).includes('BreadcrumbList') && node['@id']);
  const hasPage = nodes.some(node => typesOf(node).some(type => webPageTypes.has(type) && type !== 'FAQPage'));
  const pageSchemas = canonical && !noindex && !hasPage ? [...schemas, {
    '@context': 'https://schema.org', '@type': 'WebPage', '@id': canonical + '#webpage',
    url: canonical, name: title, description, inLanguage: 'en-IN',
    isPartOf: { '@id': ORIGIN + '/#website' }, publisher: { '@id': ORIGIN + '/#localbusiness' },
    ...(primary ? { mainEntity: { '@id': primary['@id'] } } : {}),
    ...(breadcrumb ? { breadcrumb: { '@id': breadcrumb['@id'] } } : {}),
  }] : schemas;
  const localizedSchemas = localizeSchema(pageSchemas, language);
  return <>
    {title && <title>{t(title)}</title>}
    {description && <meta name="description" content={t(description)} />}
    {keywords && <meta name="keywords" content={t(keywords)} />}
    <meta name="robots" content={effectiveRobots} />
    {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
    {canonicalUrl && !noindex && <>
      <link rel="alternate" hrefLang="en" href={english} />
      <link rel="alternate" hrefLang="hi" href={hindi} />
      <link rel="alternate" hrefLang="x-default" href={english} />
    </>}
    {title && <meta property="og:title" content={t(title)} />}
    {description && <meta property="og:description" content={t(description)} />}
    {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
    <meta property="og:type" content={ogType} /><meta property="og:site_name" content="VVPSS Taxi Services" />
    <meta property="og:locale" content={language === 'hi' ? 'hi_IN' : 'en_IN'} />
    <meta property="og:locale:alternate" content={language === 'hi' ? 'en_IN' : 'hi_IN'} />
    {image && <meta property="og:image" content={imageUrl(image)} />}
    {imageAlt && <meta property="og:image:alt" content={t(imageAlt)} />}
    <meta name="twitter:card" content="summary_large_image" />
    {title && <meta name="twitter:title" content={t(title)} />}
    {description && <meta name="twitter:description" content={t(description)} />}
    {image && <meta name="twitter:image" content={imageUrl(image)} />}
    {imageAlt && <meta name="twitter:image:alt" content={t(imageAlt)} />}
    {localizedSchemas.map((schema, index) => <script key={index} type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema).replace(/</g, '\\u003c') }} />)}
  </>;
}
